import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useTable, useSortBy, useFilters, useGlobalFilter } from 'react-table';
import './RsvpTable.css';

// Custom filter component for status
function StatusFilter({ column }) {
  const { filterValue, setFilter } = column;
  
  return (
    <select
      value={filterValue || ''}
      onChange={e => setFilter(e.target.value || undefined)}
      className="status-filter"
    >
      <option value="">All RSVPs</option>
      <option value="attending">Attending</option>
      <option value="not yet">Not Yet</option>
      <option value="false">Cannot Attend</option>
    </select>
  );
}

function RsvpTable({ data, refreshData }) {
  const [editingItem, setEditingItem] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [nameToEmailMap, setNameToEmailMap] = useState({});

// Add this useEffect to generate the name-to-email mapping when data changes
useEffect(() => {
  if (data && data.length > 0) {
    const mapping = {};
    
    // Create mappings from all entries in the data
    data.forEach(item => {
      const fullName = `${item.firstName || ''} ${item.lastName || ''}`.trim();
      const email = item.id; // Assuming the ID is the email
      
      if (fullName && email) {
        // Add name to email mapping (lowercase for case-insensitive matching)
        mapping[fullName.toLowerCase()] = email;
      }
    });
    
    setNameToEmailMap(mapping);
  }
}, [data]);

const handleEdit = useCallback((id) => {
  const itemToEdit = data.find(item => item.id === id || (item.id && item.id.S === id));
  if (itemToEdit) {
    // Create a copy of the item to edit
    const editableItem = { ...itemToEdit };
    
    // Extract email addresses from the group - focus only on Set handling
    let groupEmails = [];
    if (editableItem.group) {
      // Handle JavaScript Set object
      if (editableItem.group instanceof Set) {
        groupEmails = Array.from(editableItem.group);
      }
    }
    
    console.log('Extracted group emails:', groupEmails);
    
    // Fill group with enough empty strings to have 5 entries
    const filledGroup = [...groupEmails];
    while (filledGroup.length < 5) {
      filledGroup.push('');
    }
    
    // Process all fields to extract values from DynamoDB format
    const processedItem = {};
    
    // Special handling for group - always set it directly
    processedItem.group = filledGroup;
    
    // Handle other fields
    Object.entries(editableItem).forEach(([key, value]) => {
      if (key === 'group') {
        // Skip, already handled above
        return;
      }
      
      // Handle different DynamoDB types
      if (value && typeof value === 'object') {
        if (value instanceof Set) {
          processedItem[key] = Array.from(value);
        }
        else if (value.S) processedItem[key] = value.S;
        else if (value.N) processedItem[key] = Number(value.N);
        else if (value.BOOL !== undefined) processedItem[key] = value.BOOL;
        else processedItem[key] = value;
      } else {
        processedItem[key] = value;
      }
    });
    
    setEditingItem(itemToEdit);
    setEditFormData(processedItem);
    setShowModal(true);
  }
}, [data]);

  const handleGroupChange = useCallback((index, value) => {
    // Only auto-replace with email when selecting from suggestions,
    // not during regular typing
    setEditFormData(prev => {
      const newGroup = [...prev.group];
      newGroup[index] = value; // Use the raw value during typing
      return {
        ...prev,
        group: newGroup
      };
    });
  }, []);

  const GroupMemberInput = ({ value, onChange, index }) => {
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [inputValue, setInputValue] = useState(value || '');
    const inputRef = useRef(null);
    const suggestionsRef = useRef(null);
    
    // Keep our internal inputValue in sync with the parent's value
    useEffect(() => {
      setInputValue(value || '');
    }, [value]);
    
    const handleInputChange = (e) => {
      const newValue = e.target.value;
      setInputValue(newValue);
      
      // Generate suggestions when user types
      if (newValue.trim().length > 1) {
        console.log('Generating suggestions for:', newValue);
        console.log('Name map size:', Object.keys(nameToEmailMap).length);
        
        const matchingSuggestions = Object.entries(nameToEmailMap)
          .filter(([name, email]) => {
            const match = name.toLowerCase().includes(newValue.toLowerCase()) || 
              email.toLowerCase().includes(newValue.toLowerCase());
            return match;
          })
          .map(([name, email]) => ({ name, email }));
        
        console.log('Found suggestions:', matchingSuggestions.length);
        setSuggestions(matchingSuggestions.slice(0, 5)); // Limit to 5 suggestions
        setShowSuggestions(matchingSuggestions.length > 0);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    };
    
    // Remove the debounce effect completely - don't update parent during typing
    // useEffect(() => {
    //   const timeoutId = setTimeout(() => {
    //     onChange(inputValue);
    //   }, 50000);
    //   
    //   return () => clearTimeout(timeoutId);
    // }, [inputValue, onChange]);
    
    const handleSelectSuggestion = (email) => {
      setInputValue(email);
      setShowSuggestions(false);
      onChange(email); // Immediately update parent only when a suggestion is selected
    };
    
    // Add an onBlur handler to update parent when focus is lost
    const handleBlur = () => {
      // Update parent with current value when input loses focus
      onChange(inputValue);
    };
    
    // Keep track of whether we should ignore the next click outside
    const ignoreBlurRef = useRef(false);
    
    // Handle input focus to show suggestions again
    const handleFocus = () => {
      if (inputValue.trim().length > 1 && suggestions.length > 0) {
        setShowSuggestions(true);
      }
    };

    const handleCloseModal = useCallback(() => {
      setShowModal(false);
      setEditingItem(null);
      setEditFormData({});
      setSaveError(null);
    }, []);
    

    
      // Close suggestions when clicking outside both the input and suggestions
    useEffect(() => {
      const handleClickOutside = (event) => {
        // If we're clicking on a suggestion, don't hide yet
        if (suggestionsRef.current && suggestionsRef.current.contains(event.target)) {
          return;
        }
        
        // If we're clicking on the input, don't hide
        if (inputRef.current && inputRef.current.contains(event.target)) {
          return;
        }
        
        // Otherwise, hide suggestions
        setShowSuggestions(false);
      };
      
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);
    
    return (
      <div className="autocomplete-container">
        <input
          ref={inputRef}
          type="text"
          id={`group-email-${index}`}
          placeholder="Enter name or email"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur} // Add onBlur to update parent when input loses focus
          autoComplete="off" // Prevent browser autocomplete from interfering
        />
        {showSuggestions && suggestions.length > 0 && (
          <ul 
            className="suggestion-list"
            ref={suggestionsRef}
          >
            {suggestions.map((suggestion, idx) => (
              <li 
                key={idx} 
                onClick={() => handleSelectSuggestion(suggestion.email)}
                className="suggestion-item"
                // Prevent default to keep focus on input
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <span className="suggestion-name">{suggestion.name}</span>
                <span className="suggestion-email">{suggestion.email}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setEditingItem(null);
    setEditFormData({});
    setSaveError(null);
  }, []);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const handleCreate = useCallback(() => {
    // Initialize with empty form data and default values
    const newRsvpData = {
      id: '', // Email will be used as ID
      firstName: '',
      lastName: '',
      rsvp: 'not yet',
      cannot_attend: 'not yet',
      mealChoice:'',
      address_street: '',
      address_city: '',
      address_state: '',
      address_zip: '',
      status: 'Save the Date Received',
      isPrimary: false,
      group: ['', '', '', '', '']  // 5 empty slots for group members
    };
    
    setEditingItem(null); // No existing item to edit
    setEditFormData(newRsvpData);
    setShowModal(true);
  }, []);

  const handleSelectChange = useCallback((e) => {
    const { name, value } = e.target;
    let processedValue = value;
    
    // Handle RSVP field specifically
    if (name === 'rsvp') {
      if (value === 'true') {
        processedValue = 'attending'; // Save as "attending" instead of true
      } else if (value === 'false') {
        processedValue = false;
      } else {
        processedValue = 'not yet';
      }
      
      // Clear meal choice if RSVP is changed to "No" or "Not Yet"
      if (value !== 'true') {
        setEditFormData(prev => ({
          ...prev,
          [name]: processedValue,
          mealChoice: '' // Clear meal choice when not attending
        }));
        return; // Exit early since we've updated the state
      }
    }
    
    // Handle cannot_attend field
    if (name === 'cannot_attend') {
      if (value === 'true') processedValue = true;
      else if (value === 'false') processedValue = false;
    }
    
    setEditFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
  }, []);

  const handleSave = useCallback(async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setSaveError(null);
    
    try {
      // IMPORTANT: First, ensure all group input values are captured
      // by forcing any pending input blur events to process
      const groupInputs = document.querySelectorAll('.autocomplete-container input');
      if (groupInputs.length > 0) {
        // Force blur events to capture current values
        groupInputs.forEach(input => {
          input.dispatchEvent(new Event('blur', { bubbles: true }));
        });
        
        // Wait a moment for state updates to process
        await new Promise(resolve => setTimeout(resolve, 50));
      }
      
      // Create AWS SDK clients
      const { DynamoDBClient } = await import("@aws-sdk/client-dynamodb");
      const { DynamoDBDocumentClient, UpdateCommand, PutCommand } = await import("@aws-sdk/lib-dynamodb");
      const { fromCognitoIdentityPool } = await import("@aws-sdk/credential-provider-cognito-identity");
      const { CognitoIdentityClient } = await import("@aws-sdk/client-cognito-identity");
      const { Amplify } = await import('aws-amplify');
      
      // Create a clean copy of the form data
      const cleanedFormData = { ...editFormData };

      // Convert ID (primary email) to lowercase
    if (cleanedFormData.id) {
      cleanedFormData.id = cleanedFormData.id.toLowerCase().trim();
    }
      
      // Validate required fields for new entries
      if (!editingItem && (!cleanedFormData.id || cleanedFormData.id.trim() === '')) {
        throw new Error("Email ID is required for new RSVPs");
      }
      
      // Filter out empty strings from the group array
      if (cleanedFormData.group && Array.isArray(cleanedFormData.group)) {
        const filteredGroup = cleanedFormData.group.filter(email => email && email.trim() !== '');
        
        // If the group is empty after filtering, remove it completely
        if (filteredGroup.length === 0) {
          delete cleanedFormData.group;
        } else {
          // Set it as a proper Set object for DynamoDB
          cleanedFormData.group = new Set(filteredGroup);
        }
      }
      
      // Add timestamp for new records
      if (!editingItem && !cleanedFormData.timestamp) {
        cleanedFormData.timestamp = new Date().toISOString();
      }
      
      // Get the AWS configuration
      const awsconfig = await import('../aws-exports').then(module => module.default);
      
      // Get credentials from Amplify Auth
      const currentSession = await Amplify.Auth.currentSession();
      const idToken = currentSession.getIdToken().getJwtToken();
      
      // Configure credential provider
      const region = awsconfig.aws_project_region;
      const identityPoolId = awsconfig.aws_cognito_identity_pool_id;
      
      // Create credentials provider
      const loginKey = `cognito-idp.${region}.amazonaws.com/${awsconfig.aws_user_pools_id}`;
      const logins = {};
      logins[loginKey] = idToken;
      
      const credentialsProvider = fromCognitoIdentityPool({
        client: new CognitoIdentityClient({ region }),
        identityPoolId: identityPoolId,
        logins: logins
      });
  
      // Create DynamoDB client
      const dynamoClient = new DynamoDBClient({
        region,
        credentials: credentialsProvider
      });
      
      // Create DynamoDB Document client
      const docClient = DynamoDBDocumentClient.from(dynamoClient, {
        marshallOptions: { removeUndefinedValues: true }
      });
      
      if (editingItem) {
        // EXISTING ENTRY - UPDATE OPERATION
        // Prepare the update expression and attribute values
        const updateExpressionParts = [];
        const expressionAttributeNames = {};
        const expressionAttributeValues = {};
        
        // Use cleanedFormData instead of editFormData
        Object.keys(cleanedFormData).forEach(key => {
          if (key !== 'id') { // Skip the primary key
            updateExpressionParts.push(`#${key} = :${key}`);
            expressionAttributeNames[`#${key}`] = key;
            expressionAttributeValues[`:${key}`] = cleanedFormData[key];
          }
        });
        
        const updateCommand = new UpdateCommand({
          TableName: 'erikaandrew_wedding',
          Key: { id: cleanedFormData.id },
          UpdateExpression: `SET ${updateExpressionParts.join(', ')}`,
          ExpressionAttributeNames: expressionAttributeNames,
          ExpressionAttributeValues: expressionAttributeValues,
          ReturnValues: 'ALL_NEW'
        });
        
        // Execute update command
        await docClient.send(updateCommand);
      } else {
        // NEW ENTRY - CREATE OPERATION
        const putCommand = new PutCommand({
          TableName: 'erikaandrew_wedding',
          Item: cleanedFormData
        });
        
        // Execute put command
        await docClient.send(putCommand);
      }
      
      // Refresh the data
      refreshData();
      
      // Close the modal and reset form
      handleCloseModal();
    } catch (err) {
      console.error('Error saving RSVP data:', err);
      setSaveError(`Failed to save: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  }, [editFormData, editingItem, refreshData, handleCloseModal]);

  const getDisplayValue = useCallback((value, type = 'text') => {
    if (type === 'boolean') {
      if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
      } else if (value === 'not yet') {
        return 'Not Yet';
      } else {
        return String(value || 'No');
      }
    }
    return value || '';
  }, []);

  const debugGroupValue = (value) => {
    console.log('Group value:', {
      value,
      type: typeof value,
      isArray: Array.isArray(value),
      hasSS: value && typeof value === 'object' ? 'SS' in value : false,
      SSType: value && value.SS ? typeof value.SS : 'N/A',
      SSIsArray: value && value.SS ? Array.isArray(value.SS) : false
    });
    return value;
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Actions',
        id: 'actions',
        Cell: ({ row }) => (
          <button 
            onClick={() => handleEdit(row.original.id)}
            className="edit-button"
          >
            Edit
          </button>
        )
      },
      {
        Header: 'ID',
        accessor: 'id',
      },
      {
        Header: 'Primary',
        accessor: 'isPrimary',
        Cell: ({ value }) => value === true ? '✓' : '',
      },
      {
        Header: 'Name',
        id: 'fullName',
        accessor: row => `${row.firstName || ''} ${row.lastName || ''}`,
      },
      {
        Header: 'First Name',
        accessor: 'firstName',
      },
      {
        Header: 'Last Name',
        accessor: 'lastName',
      },
      {
        Header: 'Meal Choice',
        accessor: 'mealChoice',
        Cell: ({ value }) => {
          switch(value) {
            case 'beef_salmon': return 'Surf and Turf';
            case 'vegetarian': return 'Vegetarian';
            case 'kids_meal': return 'Kids Meal';
            default: return value || 'Not selected';
          }
        }
      },
      {
        Header: 'Group',
        accessor: 'group',
        Cell: ({ value }) => {
          // Focus only on Set handling
          if (value instanceof Set) {
            return Array.from(value).join(', ');
          }
          
          // Return empty string for null/undefined values
          return '';
        }
      },
      {
        Header: 'RSVP',
        accessor: 'rsvp',
        Cell: ({ value }) => getDisplayValue(value, 'boolean'),
        Filter: StatusFilter,
      },
      {
        Header: 'Cannot Attend',
        accessor: 'cannot_attend',
        Cell: ({ value }) => getDisplayValue(value, 'boolean')
      },
      {
        Header: 'Address',
        id: 'fullAddress',
        accessor: row => {
          const street = row.address_street || '';
          const city = row.address_city || '';
          const state = row.address_state || '';
          const zip = row.address_zip || '';
          return [street, city, state, zip].filter(Boolean).join(', ');
        },
      },
      {
        Header: 'Status',
        accessor: 'status',
        Filter: StatusFilter,
      },
      {
        Header: 'Timestamp',
        accessor: 'timestamp',
        Cell: ({ value }) => value ? new Date(value).toLocaleString() : '',
      },
    ],
    [handleEdit, getDisplayValue]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state,
    setGlobalFilter,
    setFilter,
  } = useTable(
    {
      columns,
      data,
    },
    useFilters,
    useGlobalFilter,
    useSortBy
  );

  return (
    <div className="rsvp-table-container">
      <div className="table-controls">
        <div className="search-section">
          <input
            value={state.globalFilter || ''}
            onChange={e => setGlobalFilter(e.target.value || undefined)}
            placeholder="Search all fields..."
            className="search-input"
          />
        </div>
        <div className="filter-section">
          <label htmlFor="rsvp-filter">Filter by RSVP:</label>
          <select
            id="rsvp-filter"
            value={state.filters.find(f => f.id === 'rsvp')?.value || ''}
            onChange={e => {
              const rsvpFilter = e.target.value;
              setFilter('rsvp', rsvpFilter || undefined);
            }}
            className="status-filter-dropdown"
          >
            <option value="">All RSVPs</option>
            <option value="attending">Attending</option>
            <option value="not yet">Not Yet</option>
            <option value="cannot attend">cannot attend</option>
          </select>
        </div>
        <div className="action-buttons">
          <button onClick={refreshData} className="refresh-button">
            Refresh Data
          </button>
          <button onClick={() => handleCreate()} className="create-button">
            Create RSVP
          </button>
        </div>
      </div>
      
      <div className="table-stats">
        <p>Total RSVPs: {data.length}</p>
        <p>Attending: {data.filter(item => item.rsvp === "attending").length}</p>
        <p>Not Attending: {data.filter(item => item.cannot_attend === true).length}</p>
        <p>Pending: {data.filter(item => 
          item.rsvp === 'not yet' || 
          (item.rsvp !== "attending" && item.cannot_attend !== true)
        ).length}</p>
      </div>
      
      <div className="table-wrapper">
        <table {...getTableProps()} className="rsvp-table">
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                    {column.render('Header')}
                    <span>
                      {column.isSorted
                        ? column.isSortedDesc
                          ? ' 🔽'
                          : ' 🔼'
                        : ''}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map(row => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map(cell => (
                    <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Edit Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
          <div className="modal-header">
            <h3>{editingItem ? 'Edit RSVP' : 'Create New RSVP'}</h3>
            <button onClick={handleCloseModal} className="close-button">&times;</button>
          </div>
            
            {saveError && (
              <div className="error-message">
                {saveError}
              </div>
            )}
            
            <form onSubmit={handleSave} className="edit-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First Name</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={editFormData.firstName || ''}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="lastName">Last Name</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={editFormData.lastName || ''}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="id">Email Address (ID)</label>
                <input
                  type="email"
                  id="id"
                  name="id"
                  value={editFormData.id || ''}
                  onChange={handleInputChange}
                  disabled={editingItem} // Disable editing for existing entries
                  required={!editingItem} // Make it required for new entries
                />
                {!editingItem && <p className="form-help">Email address is required and will be used as the ID</p>}
              </div>

              <div className="form-group checkbox-group">
                <input
                  type="checkbox"
                  id="isPrimary"
                  name="isPrimary"
                  checked={editFormData.isPrimary || false}
                  onChange={(e) => {
                    setEditFormData(prev => ({
                      ...prev,
                      isPrimary: e.target.checked
                    }));
                  }}
                />
                <label htmlFor="isPrimary">Primary Contact</label>
                <p className="form-help">Designate this person as the primary contact for this group</p>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="rsvp">RSVP</label>
                  <select
                    id="rsvp"
                    name="rsvp"
                    value={(() => {
                      // Convert "attending" to "true" for the select dropdown
                      if (editFormData.rsvp === 'attending') return 'true';
                      if (editFormData.rsvp === false) return 'false';
                      return editFormData.rsvp || 'not yet';
                    })()}
                    onChange={handleSelectChange}
                  >
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                    <option value="not yet">Not Yet</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="cannot_attend">Cannot Attend</label>
                  <select
                    id="cannot_attend"
                    name="cannot_attend"
                    value={editFormData.cannot_attend?.toString() || 'not yet'}
                    onChange={handleSelectChange}
                  >
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                    <option value="not yet">Not Yet</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="mealChoice">Meal Choice</label>
                <select
                  id="mealChoice"
                  name="mealChoice"
                  value={editFormData.mealChoice || ''}
                  onChange={handleInputChange}
                  // disabled={editFormData.rsvp !== 'true'} // Only enable if attending
                >
                  <option value="">Select meal...</option>
                  <option value="beef_salmon">Surf and Turf</option>
                  <option value="vegetarian">Vegetarian</option>
                  <option value="kids_meal">Kids Meal</option>
                </select>
                {editFormData.rsvp === 'attending' && 
                  <p className="form-help">Please select a meal choice for this guest</p>
                }
              </div>
              
              <div className="form-group">
                <label htmlFor="address_street">Street</label>
                <input
                  type="text"
                  id="address_street"
                  name="address_street"
                  value={editFormData.address_street || ''}
                  onChange={handleInputChange}
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="address_city">City</label>
                  <input
                    type="text"
                    id="address_city"
                    name="address_city"
                    value={editFormData.address_city || ''}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="address_state">State</label>
                  <input
                    type="text"
                    id="address_state"
                    name="address_state"
                    value={editFormData.address_state || ''}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="address_zip">ZIP</label>
                  <input
                    type="text"
                    id="address_zip"
                    name="address_zip"
                    value={editFormData.address_zip || ''}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="status">Status</label>
                <input
                  type="text"
                  id="status"
                  name="status"
                  value={editFormData.status || ''}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>Group Members (Emails)</label>
                <div className="group-emails-container">
                  {Array.isArray(editFormData.group) ? (
                    editFormData.group.map((email, index) => (
                      <div key={index} className="email-input-container">
                        <GroupMemberInput 
                          value={email}
                          onChange={(value) => handleGroupChange(index, value)}
                          index={index}
                        />
                      </div>
                    ))
                  ) : (
                    Array.from({ length: 5 }).map((_, index) => (
                      <div key={index} className="email-input-container">
                        <GroupMemberInput 
                          value=""
                          onChange={(value) => {
                            if (!Array.isArray(editFormData.group)) {
                              setEditFormData(prev => ({
                                ...prev,
                                group: Array(5).fill('')
                              }));
                            }
                            handleGroupChange(index, value);
                          }}
                          index={index}
                        />
                      </div>
                    ))
                  )}
                </div>
                <p className="form-help">Enter name or email, leave fields empty to remove</p>
              </div>
              <div className="form-actions">
                <button 
                  type="button" 
                  onClick={handleCloseModal}
                  className="cancel-button"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="save-button"
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default RsvpTable;